import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Col,
  Collapse,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';
import AdminStoreSearchCombobox from './AdminStoreSearchCombobox';
import './AdminReportsPage.css';

const PVZ_OPTIONS = [
  { value: 'all', labelKey: 'adminReports.pvzAll' },
  { value: 'shosseynaya', labelKey: 'adminReports.pvzShosseynaya' },
  { value: 'proezd_3723', labelKey: 'adminReports.pvzProezd' },
];

function currentIsoWeek() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  const weekNum = 1 + Math.round(((d - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
  return `${d.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}

function defaultTrimmedDebtDate() {
  const d = new Date();
  d.setDate(d.getDate() - 3);
  return d.toISOString().slice(0, 10);
}

function StatusIcon({ ok }) {
  return ok ? (
    <span className="text-success"><i className="bi bi-check-circle-fill" /></span>
  ) : (
    <span className="text-danger"><i className="bi bi-x-circle-fill" /></span>
  );
}

function MetricCard({ value, label, hint }) {
  return (
    <div className="admin-reports-metric-card">
      <h3>{value ?? '—'}</h3>
      <p>{label}</p>
      {hint ? <p className="text-muted small mt-1 mb-0">{hint}</p> : null}
    </div>
  );
}

function ReportSections({ report, t, onExport, week, day, storeId, pvzScope }) {
  if (!report) return null;
  const exp = (metric) => onExport({ metric, week, day, store_id: storeId, pvz_scope: pvzScope });

  return (
    <div>
      <Row className="g-3 mb-3">
        <Col md={4}>
          <MetricCard value={report.receipts?.count} label={t('adminReports.metricReceipts')} />
          <Button size="sm" variant="outline-success" className="mt-2" onClick={() => exp('receipts')}>
            Excel
          </Button>
        </Col>
        <Col md={4}>
          <MetricCard value={report.wb_received?.count} label={t('adminReports.metricWbReceived')} />
        </Col>
        <Col md={4}>
          <MetricCard
            value={report.avg_accept_hours != null ? `${report.avg_accept_hours} ч` : '—'}
            label={t('adminReports.metricAvgAccept')}
            hint={report.avg_accept_sample ? `n=${report.avg_accept_sample}` : ''}
          />
        </Col>
      </Row>

      <h6 className="admin-reports-section-title">{t('adminReports.sectionUsers')}</h6>
      <Row className="g-2 mb-2 small">
        <Col md={3}>{t('adminReports.newUsers')}: <strong>{report.users_stores?.new_users}</strong></Col>
        <Col md={3}>{t('adminReports.usersLoggedIn')}: <strong>{report.users_stores?.users_logged_in}</strong></Col>
        <Col md={3}>{t('adminReports.newStores')}: <strong>{report.users_stores?.new_stores}</strong></Col>
        <Col md={3}>{t('adminReports.avgDailyLogins')}: <strong>{report.users_stores?.avg_daily_logins}</strong></Col>
      </Row>
      <p className="small text-muted">
        {t('adminReports.unregisteredWb')}: <strong>{report.unregistered_wb?.total}</strong>
        {' — '}{t('adminReports.unregisteredWbHint')}
      </p>
      {(report.unregistered_wb?.stores || []).slice(0, 10).map((s) => (
        <div key={s.store_id} className="small mb-1">
          <code>{s.store_id}</code>:{' '}
          {(s.product_links || []).map((url, i) => (
            <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="me-2">
              WB{i + 1}
            </a>
          ))}
        </div>
      ))}

      <h6 className="admin-reports-section-title">{t('adminReports.sectionBilling')}</h6>
      <Row className="g-2 mb-2 small">
        <Col md={4}>{t('adminReports.topUps')}: <strong>¥{report.billing?.top_ups_total}</strong> ({report.billing?.top_ups_count})</Col>
        <Col md={4}>{t('adminReports.charges')}: <strong>¥{report.billing?.charges_total}</strong> ({report.billing?.charges_count})</Col>
        <Col md={4}>{t('adminReports.debtors')}: <strong>{report.billing?.debtors?.count}</strong></Col>
      </Row>
      <Table size="sm" bordered className="small mb-2">
        <thead><tr><th>{t('adminReports.chargeType')}</th><th>#</th><th>¥</th></tr></thead>
        <tbody>
          {(report.billing?.by_type || []).filter((x) => x.count > 0).map((x) => (
            <tr key={x.type}><td>{x.label}</td><td>{x.count}</td><td>{x.total}</td></tr>
          ))}
        </tbody>
      </Table>
      {(report.billing?.debtors?.rows || []).length > 0 && (
        <>
          <Table size="sm" bordered className="small">
            <thead>
              <tr>
                <th>{t('adminReports.colStore')}</th>
                <th>{t('adminReports.colDebt')}</th>
                <th>{t('adminReports.colLastPayment')}</th>
              </tr>
            </thead>
            <tbody>
              {report.billing.debtors.rows.map((r) => (
                <tr key={r.store_id} className={r.stale ? 'admin-reports-debtor-stale' : ''}>
                  <td>{r.store_name} <code className="ms-1">{r.store_id}</code></td>
                  <td>¥{r.debt}</td>
                  <td>{r.last_payment_date || '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="small fw-semibold">{t('adminReports.totalDebt')}: ¥{report.billing.debtors.total_debt}</p>
        </>
      )}

      <h6 className="admin-reports-section-title">{t('adminReports.sectionProblems')}</h6>
      <Row className="g-2 small">
        <Col md={6}>
          {t('adminReports.anomalyAcceptBefore')}: <Badge bg="warning">{report.problems?.accept_before_completed}</Badge>
          <Button size="sm" variant="link" onClick={() => exp('accept_before_completed')}>Excel</Button>
        </Col>
        <Col md={6}>
          {t('adminReports.noReceiptCompleted')}: <Badge bg="danger">{report.problems?.completed_no_receipt}</Badge>
          <Button size="sm" variant="link" onClick={() => exp('completed_no_receipt')}>Excel</Button>
        </Col>
        <Col md={6}>
          {t('adminReports.completed72h')}: <Badge bg="danger">{report.problems?.completed_72h_no_accept}</Badge>
          <Button size="sm" variant="link" onClick={() => exp('completed_72h_no_accept')}>Excel</Button>
        </Col>
        <Col md={6}>
          {t('adminReports.acceptedInTransit')}: <Badge bg="info">{report.problems?.accepted_status_in_transit}</Badge>
        </Col>
      </Row>
    </div>
  );
}

const AdminReportsPage = () => {
  const { t } = useLanguage();
  const [week, setWeek] = useState(currentIsoWeek);
  const [storeId, setStoreId] = useState('');
  const [pvzScope, setPvzScope] = useState('all');
  const [trimmedDebtAfter, setTrimmedDebtAfter] = useState(defaultTrimmedDebtDate);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [issuedFrom, setIssuedFrom] = useState('');
  const [issuedTo, setIssuedTo] = useState('');
  const [issuedRows, setIssuedRows] = useState([]);
  const [issuedLoading, setIssuedLoading] = useState(false);

  const filterParams = useMemo(() => ({
    week,
    store_id: storeId || undefined,
    pvz_scope: pvzScope === 'all' ? undefined : pvzScope,
  }), [week, storeId, pvzScope]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rep, pul] = await Promise.all([
        api.getAdminReports(filterParams),
        api.getAdminReportsPulse({ trimmed_debt_after: trimmedDebtAfter }),
      ]);
      setReportData(rep?.data ?? rep);
      setPulse(pul?.data ?? pul);
    } catch (err) {
      setError(err?.message || t('adminReports.loadError'));
    } finally {
      setLoading(false);
    }
  }, [filterParams, trimmedDebtAfter, t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleExport = async (params) => {
    setExporting(true);
    try {
      const res = await api.downloadAdminReportsExcel(params);
      const blob = res?.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${params.metric}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.message || t('adminReports.exportError'));
    } finally {
      setExporting(false);
    }
  };

  const handlePdf = async (day) => {
    try {
      const res = await api.downloadAdminReportsPdf({
        ...filterParams,
        day: day || undefined,
      });
      const blob = res?.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = day ? `report_${day}.pdf` : `report_${week}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.message || t('adminReports.exportError'));
    }
  };

  const loadIssued = async () => {
    if (!issuedFrom || !issuedTo) return;
    setIssuedLoading(true);
    try {
      const res = await api.getAdminReportsIssuedGoods({
        date_from: issuedFrom,
        date_to: issuedTo,
        store_id: storeId || undefined,
        pvz_scope: pvzScope === 'all' ? undefined : pvzScope,
      });
      const data = res?.data ?? res;
      setIssuedRows(data?.rows || []);
    } catch (err) {
      setError(err?.message || t('adminReports.loadError'));
    } finally {
      setIssuedLoading(false);
    }
  };

  const downloadErrorsLog = async () => {
    try {
      const res = await api.downloadAdminReportsErrorsLog();
      const blob = res?.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'errors_log.txt';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.message || t('adminReports.exportError'));
    }
  };

  return (
    <div className="admin-reports-page">
      <h2 className="h4 mb-3">{t('adminReports.title')}</h2>

      <div className="admin-reports-filters">
        <Form onSubmit={(e) => { e.preventDefault(); loadAll(); }}>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Label className="small">{t('adminReports.store')}</Form.Label>
              <AdminStoreSearchCombobox
                filterValue={storeId}
                onFilterValueChange={setStoreId}
                valueField="store_id"
                searchExtras={{ store_type: 'wildberries', has_api_key: true }}
                placeholder={t('adminReports.storePlaceholder')}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small">{t('adminReports.week')}</Form.Label>
              <Form.Control type="week" value={week} onChange={(e) => setWeek(e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label className="small">{t('adminReports.pvz')}</Form.Label>
              <Form.Select value={pvzScope} onChange={(e) => setPvzScope(e.target.value)}>
                {PVZ_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md="auto">
              <Button type="submit" className="btn-gradient" disabled={loading}>
                {loading ? <Spinner size="sm" className="me-2" /> : null}
                {t('adminReports.apply')}
              </Button>
            </Col>
          </Row>
        </Form>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <h5 className="mb-3">{t('adminReports.pulseTitle')}</h5>
      <Row className="g-2 mb-2 align-items-end">
        <Col md={3}>
          <Form.Label className="small">{t('adminReports.trimmedDebtAfter')}</Form.Label>
          <Form.Control
            type="date"
            value={trimmedDebtAfter}
            onChange={(e) => setTrimmedDebtAfter(e.target.value)}
          />
          <Form.Text className="text-muted">{t('adminReports.trimmedDebtHint')}</Form.Text>
        </Col>
        <Col md="auto">
          <Button size="sm" variant="outline-primary" onClick={loadAll}>{t('adminReports.refreshPulse')}</Button>
        </Col>
      </Row>
      {pulse && (
        <div className="admin-reports-pulse-grid mb-4">
          <MetricCard value={pulse.accepted_returns_total} label={t('adminReports.pulseAccepted')} />
          <MetricCard value={pulse.total_debt} label={t('adminReports.pulseTotalDebt')} hint={t('adminReports.pulseTotalDebtHint')} />
          <MetricCard value={pulse.trimmed_debt} label={t('adminReports.pulseTrimmedDebt')} />
          <MetricCard value={`¥${pulse.top_ups_total}`} label={t('adminReports.pulseTopUps')} />
          <MetricCard value={`¥${pulse.charges_total}`} label={t('adminReports.pulseCharges')} />
          <MetricCard value={pulse.active_users_96h} label={t('adminReports.pulseActiveUsers')} />
          <MetricCard value={pulse.support_messages_24h} label={t('adminReports.pulseSupport')} />
          <MetricCard
            value={pulse.tech?.celery_failed_24h}
            label={t('adminReports.pulseErrors')}
            hint={t('adminReports.pulseErrorsHint')}
          />
        </div>
      )}
      {pulse?.tech && (
        <p className="small text-muted mb-4">
          {t('adminReports.techLoad')}: {pulse.tech.load_avg?.join(', ') || '—'}
          {' · '}{t('adminReports.techMem')}: {pulse.tech.process_memory_mb ?? '—'} MB
          {' · '}
          <Button size="sm" variant="link" className="p-0 align-baseline" onClick={downloadErrorsLog}>
            {t('adminReports.downloadErrorsLog')}
          </Button>
        </p>
      )}

      {reportData && (
        <>
          <div className="admin-reports-week-card">
            <div className="card-header-gradient d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <h4 className="mb-0 text-white">{t('adminReports.weekCardTitle')}</h4>
                <small>{reportData.week_start} — {reportData.week_end}</small>
              </div>
              <Button size="sm" variant="light" onClick={() => handlePdf(null)} disabled={exporting}>
                <i className="bi bi-file-pdf me-1" /> PDF
              </Button>
            </div>
            <ReportSections
              report={reportData.week_report}
              t={t}
              onExport={handleExport}
              week={week}
              storeId={storeId}
              pvzScope={pvzScope}
            />
          </div>

          <h5 className="mb-3">{t('adminReports.dailyTitle')}</h5>
          <div className="admin-reports-day-grid mb-4">
            {(reportData.days || []).map((d) => (
              <div
                key={d.date}
                className={`admin-reports-day-card ${expandedDay === d.date ? 'expanded' : ''}`}
                onClick={() => setExpandedDay(expandedDay === d.date ? null : d.date)}
                onKeyDown={(e) => e.key === 'Enter' && setExpandedDay(expandedDay === d.date ? null : d.date)}
                role="button"
                tabIndex={0}
              >
                <div className="day-label">{d.label}</div>
                <div className="small mt-1">
                  {t('adminReports.metricReceipts')}: <strong>{d.report?.receipts?.count}</strong>
                </div>
                <div className="small">
                  WB: <strong>{d.report?.wb_received?.count}</strong>
                </div>
                <Collapse in={expandedDay === d.date}>
                  <div className="admin-reports-day-detail" onClick={(e) => e.stopPropagation()}>
                    <ReportSections
                      report={d.report}
                      t={t}
                      onExport={handleExport}
                      week={week}
                      day={d.date}
                      storeId={storeId}
                      pvzScope={pvzScope}
                    />
                    <Button size="sm" variant="outline-secondary" className="mt-2" onClick={() => handlePdf(d.date)}>
                      PDF
                    </Button>
                  </div>
                </Collapse>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="admin-reports-issued-table">
        <h5>{t('adminReports.issuedTitle')}</h5>
        <Row className="g-2 align-items-end mb-3">
          <Col md={2}>
            <Form.Label className="small">{t('adminReports.dateFrom')}</Form.Label>
            <Form.Control type="date" value={issuedFrom} onChange={(e) => setIssuedFrom(e.target.value)} />
          </Col>
          <Col md={2}>
            <Form.Label className="small">{t('adminReports.dateTo')}</Form.Label>
            <Form.Control type="date" value={issuedTo} onChange={(e) => setIssuedTo(e.target.value)} />
          </Col>
          <Col md="auto">
            <Button variant="primary" onClick={loadIssued} disabled={issuedLoading}>
              {issuedLoading ? <Spinner size="sm" className="me-2" /> : null}
              {t('adminReports.loadIssued')}
            </Button>
          </Col>
        </Row>
        {issuedRows.length > 0 && (
          <Table striped bordered size="sm" className="small">
            <thead>
              <tr>
                <th>{t('adminReports.colAccept')}</th>
                <th>{t('adminReports.colStore')}</th>
                <th>{t('adminReports.colProduct')}</th>
                <th>srid</th>
                <th>nmId</th>
                <th>{t('adminReports.colBarcode')}</th>
                <th>{t('adminReports.colStatus')}</th>
                <th>completedDt</th>
              </tr>
            </thead>
            <tbody>
              {issuedRows.map((r) => (
                <tr key={r.snapshotId}>
                  <td className="text-center"><StatusIcon ok={r.acceptStatus} /></td>
                  <td>{r.storeName}<br /><code className="small">{r.storeSellerId}</code></td>
                  <td>
                    {r.productName}
                    {r.productUrl ? (
                      <div><a href={r.productUrl} target="_blank" rel="noopener noreferrer" className="small">WB</a></div>
                    ) : null}
                  </td>
                  <td className="text-break">{r.srid}</td>
                  <td>{r.nmId}</td>
                  <td>{r.barcode}</td>
                  <td>{r.status}</td>
                  <td className="text-nowrap">{r.completedDt}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default AdminReportsPage;
